import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Avatar, Box, ListItem, Typography } from '@mui/material';
import { Checkbox, FormControlLabel } from '@material-ui/core';

import { FormFieldValues } from 'containers/CommunityFormPage/CommunityForm/formType';
import { UserMultiSelect, Employee } from 'containers/CommunityPage/EmployeeSearch/UserMultiSelect';

type LeadSelectionUserProps = {
  onClick: () => void;
  user: Employee;
};

export const LeadSelectionUser = ({ onClick, user }: LeadSelectionUserProps) => {
  const name = [user.fullName?.first, user.fullName?.last].filter(Boolean).join(' ').trim();
  return (
    <ListItem
      key={user.id}
      style={{ display: 'flex', alignItems: 'center', gap: 10 }}
      onClick={onClick}
    >
      {user.mainPicture && (
        <Avatar alt={name} src={user.mainPicture} style={{ width: 36, height: 36 }} />
      )}
      <Box flex="1 1 auto">
        {name} <span style={{ color: '#676767' }}>{user.organizationDepartment}</span>
      </Box>
    </ListItem>
  );
};

interface LeaderListControllerProps {
  entityType?: 'team' | 'community';
}

export const LeaderListController = ({
  entityType = 'community',
}: LeaderListControllerProps): JSX.Element => {
  const { setValue } = useFormContext<FormFieldValues>();
  const isTeam = entityType === 'team';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Controller<FormFieldValues, 'leads'>
          name="leads"
          render={({ field }): JSX.Element => (
            <UserMultiSelect
              label={`Select ${isTeam ? 'team' : 'community'} leads`}
              helperText={`Search for ${isTeam ? 'team' : 'community'} leads`}
              selectedUsers={field.value}
              setSelectedUsers={(leads): void => setValue('leads', leads)}
              renderEmployee={LeadSelectionUser}
            />
          )}
        />
        {!isTeam && (
          <>
            <Controller
              name="isPrivate"
              render={({ field }): JSX.Element => (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        color="primary"
                        onChange={(_, checked) => setValue('isPrivate', checked)}
                        name="isPrivate"
                      />
                    }
                    label="Private community"
                  />
                  <Typography sx={{ ml: 4, color: 'text.secondary' }}>
                    Private communities are hidden from non-members. Only Admins or Community Leads
                    can add members.
                  </Typography>
                </Box>
              )}
            />
            <Controller
              name="allowAllyMembers"
              render={({ field }): JSX.Element => (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        color="primary"
                        onChange={(_, checked) => setValue('allowAllyMembers', checked)}
                        name="allowAllyMembers"
                      />
                    }
                    label="Allow Ally members"
                  />
                  <Typography sx={{ ml: 4, color: 'text.secondary' }}>
                    Allows supporters who stand in solidarity with this community to participate,
                    even if they don`t personally identify with it.
                  </Typography>
                </Box>
              )}
            />
            <Controller
              name="allowAnonymousMembers"
              render={({ field }): JSX.Element => (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        color="primary"
                        onChange={(_, checked) => setValue('allowAnonymousMembers', checked)}
                        name="allowAnonymousMembers"
                      />
                    }
                    label="Allow Anonymous members"
                  />
                  <Typography sx={{ ml: 4, color: 'text.secondary' }}>
                    Enables participation from those who wish to support or belong privately,
                    without revealing their identity to others.
                  </Typography>
                </Box>
              )}
            />
          </>
        )}
      </Box>
    </Box>
  );
};
